import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  FunctionComponent,
  ReactNode,
} from "react";

/** Maps system color scheme preference to theme keys. Values are theme keys, not compiled VE class names. */
export interface SystemThemeMap {
  dark: string;
  light: string;
}

interface ThemeContextValue {
  /** The active theme key, reflecting the last resolved or user-set value. */
  theme: string;
  /**
   * Sets the active theme. When `force` is true, overrides `suggestedTheme` for the current
   * page session; without it, calls on pages with a `suggestedTheme` are no-ops.
   */
  setTheme: (theme: string, force?: boolean) => void;
  /** The theme key currently applied to `document.body`. */
  resolvedTheme: string;
  /** The page-level theme suggestion from CMS (`pageProps.pageTheme`), if any. */
  suggestedTheme: string | undefined;
  /** Optional UI slot — populated by the brand app and rendered by `AdminContainer`. */
  themeSwitcherComponent: ReactNode;
  /**
   * The user's explicit theme selection, independent of the page's resolved theme.
   * `null` means no stored preference — the theme follows system preference (auto mode).
   */
  userSelectedTheme: string | null;
  /** Clears any stored theme override and returns to system-preference resolution. */
  clearThemeOverride: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface Props {
  themes: Record<string, string>;
  defaultTheme: string;
  storageKey: string;
  systemThemeMap?: SystemThemeMap;
  suggestedTheme?: string;
  themeSwitcherComponent?: ReactNode;
  children: ReactNode;
}

/** Returns a valid theme key from the `?theme=` query param, or null if absent/unrecognised. SSR-safe. */
function getQueryTheme(themes: Record<string, string>): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const q = new URLSearchParams(window.location.search).get("theme");
    return q && themes[q] ? q : null;
  } catch {
    return null;
  }
}

/** Returns the persisted theme key from localStorage, or null if unavailable (SSR or private browsing). */
function getStoredTheme(storageKey: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/** Returns the theme key matching `prefers-color-scheme`, or null if no map is provided or SSR. */
function getSystemTheme(
  systemThemeMap: SystemThemeMap | undefined,
): string | null {
  if (!systemThemeMap || typeof window === "undefined") {
    return null;
  }
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? systemThemeMap.dark
      : systemThemeMap.light;
  } catch {
    return null;
  }
}

/**
 * Manages light/dark theme state for a brand app. Applies the resolved theme as a Vanilla Extract
 * class on `document.body` using a targeted classList swap to preserve any other body classes.
 *
 * Resolution priority: `force:true` override → `?theme=` query param → `suggestedTheme` → localStorage → system preference → `defaultTheme`.
 *
 * Intended for use in the brand app's `_app.tsx`. Other brands that do not mount this provider
 * are unaffected — `useTheme()` returns null and `AdminContainer` renders nothing for the switcher slot.
 */
const ThemeProvider: FunctionComponent<Props> = ({
  themes,
  defaultTheme,
  storageKey,
  systemThemeMap,
  suggestedTheme,
  themeSwitcherComponent,
  children,
}) => {
  const [theme, setThemeState] = useState(defaultTheme);
  const [isForcedOverride, setIsForcedOverride] = useState(false);
  const [userSelectedTheme, setUserSelectedTheme] = useState<string | null>(
    null,
  );
  const currentThemeClass = useRef<string | null>(null);

  const applyClass = useCallback(
    (themeKey: string) => {
      const cls = themes[themeKey];
      if (!cls) {
        return;
      }
      if (currentThemeClass.current) {
        document.body.classList.remove(currentThemeClass.current);
      }
      document.body.classList.add(cls);
      currentThemeClass.current = cls;
      // Stable semantic theme key for external consumers (ads, third-party scripts).
      document.body.setAttribute("data-duet-theme", themeKey);
    },
    [themes],
  );

  useEffect(() => {
    const stored = getStoredTheme(storageKey);
    if (stored) {
      setUserSelectedTheme(stored);
    }
    // Empty deps: runs once on mount to sync the client-only localStorage value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isForcedOverride) {
      return;
    }
    const queryTheme = getQueryTheme(themes);
    if (queryTheme) {
      setThemeState(queryTheme);
      applyClass(queryTheme);
      return;
    }
    if (suggestedTheme && themes[suggestedTheme]) {
      setThemeState(suggestedTheme);
      applyClass(suggestedTheme);
      return;
    }
    const stored = getStoredTheme(storageKey);
    const resolved =
      (stored && themes[stored] ? stored : null) ??
      getSystemTheme(systemThemeMap) ??
      defaultTheme;
    setThemeState(resolved);
    applyClass(resolved);
  }, [
    isForcedOverride,
    suggestedTheme,
    storageKey,
    themes,
    systemThemeMap,
    defaultTheme,
    applyClass,
  ]);

  const setTheme = useCallback(
    (newTheme: string, force = false) => {
      if (suggestedTheme && !force) {
        return;
      }
      if (!themes[newTheme]) {
        return;
      }
      if (force) {
        setIsForcedOverride(true);
      }
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch {
        // localStorage unavailable
      }
      setUserSelectedTheme(newTheme);
      setThemeState(newTheme);
      applyClass(newTheme);
    },
    [suggestedTheme, storageKey, themes, applyClass],
  );

  const clearThemeOverride = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // localStorage unavailable
    }
    setUserSelectedTheme(null);
    setIsForcedOverride(false);
    // Immediately apply the auto-resolved theme to avoid a flash on the next effect run.
    const queryTheme = getQueryTheme(themes);
    const resolved =
      queryTheme ??
      (suggestedTheme && themes[suggestedTheme] ? suggestedTheme : null) ??
      getSystemTheme(systemThemeMap) ??
      defaultTheme;
    setThemeState(resolved);
    applyClass(resolved);
  }, [
    storageKey,
    themes,
    suggestedTheme,
    systemThemeMap,
    defaultTheme,
    applyClass,
  ]);

  const resolvedTheme = theme;

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        resolvedTheme,
        suggestedTheme,
        themeSwitcherComponent,
        userSelectedTheme,
        clearThemeOverride,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Returns the current theme context, or null when called outside a `ThemeProvider`.
 * Callers in shared components (e.g. `AdminContainer`) should guard against null.
 */
export const useTheme = (): ThemeContextValue | null => {
  return useContext(ThemeContext);
};

export default ThemeProvider;
